import {BehaviorSubject, Observable} from 'rxjs';
import {DataSource} from '../models/data-source.modol';
import {RelationData} from '../models/relationData.model';
import {skip} from 'rxjs/operators';
import {Injectable, signal} from '@angular/core';
import {Column} from '../models/column.model';
import {ScheduleAsset} from '../models/scheduleAsset.model';
import {ScheduleHistory} from '../models/scheduleHistory.model';
import {ScheduleType} from '../models/scheduleType.enum';
import {MonthType} from '../models/monthType.enum';
import {WeekDay} from '../models/weekDay.enum';
import {WeekDayNumber} from '../models/weekDayNumber.enum';
import {ColumnsType} from '../models/columns-type.model';
import {SqlServerConnection} from '../models/sql-server-connection.model';
import {WebServiceConnection} from '../models/web-service-connection.model';
import {Model} from '../models/model.model';
import {Relation} from '../models/relation.model';
import {Submit} from '../../data-modelling/models/submit.enum';
import {RelationState} from '../models/relation-state.type';
import {SubmitType} from '../../shared/models/submit.type';
import {CalculateColumnModel} from '../models/calculate-column.model';
import {OracleConnection} from '../models/oracle-connection.model';
import {UserFilter} from '../../shared/models/user-filter.model';
import {UserFilterRecipientType, UserOrGroup} from '../../shared/models/userOrGroup.model';
import {UserFilterDetails} from '../../shared/models/userFilterDetails.model';
import {
  UserFilterRecipients,
  UserFilterRecipientStatus,
  UserInUserFilter,
} from '../../shared/models/userInUserfilter.model';
import {ColumnList} from '../models/column-list.model';
import {DataModuleColumnService} from '../services/dataModuleColumn.service';
import {DataSourceDataModuleColumn} from '../models/dataSource-dataModule-column';
import {DataSourceConditionalColumn} from '../models/conditional-column.model';
import {PreviewDataSourceInfo} from '../models/preview-data-source-info.model';
import { DateColumnsListItem, miladiDateColumnTypes, shamsiDateColumnTypes } from '../models/add-date-columns';
import { CustomDateColumns } from '../models/custom-date-columns.model';
import { ProcessingDataSource } from '../models/processing-data-source.model';
import { DataSourceUploadProgress } from '../models/processing-data-source.model';
@Injectable()
export class DataSourceState {
  loadingDataSourceTable = false;
  loadingRelations = false;
  loadingSchedule = false;
  loadingUserFilters = false;
  loadingScheduleHistory = false;
  loadingDataSourceColumnsDataTypes = false;

  private loadingDataSource$ = new BehaviorSubject<boolean>(false);
  private mainDataSource$ = new BehaviorSubject<DataSource>(null);
  private tableContent$ = new BehaviorSubject<Column[]>(null);
  private columnsType$ = new BehaviorSubject<ColumnsType>(null);


  // relations
  private dataSourcesForRelation$ = new BehaviorSubject<Partial<DataSource>[]>(null);
  private totalPagesRelation$ = new BehaviorSubject<number>(null);
  private pageNumberRelation$ = new BehaviorSubject<number>(1);
  private createRelationState$ = new BehaviorSubject<Submit>(Submit.notSubmitted);
  private relationState$ = new BehaviorSubject<RelationState>('noState');

  // schedule
  private scheduleHistory$ = new BehaviorSubject<ScheduleHistory[]>(null);
  private schedule$ = new BehaviorSubject<ScheduleAsset>(null);
  private scheduleHistoryTotalPages$ = new BehaviorSubject<number>(null);
  private scheduleHistoryPageNumber$ = new BehaviorSubject<number>(1);

  // popUps
  private webServiceConnection$ = new BehaviorSubject<WebServiceConnection>(null);

  //calculate column
  private calculateColumnSubmit$ = new BehaviorSubject<SubmitType>('notSubmitted');
  private editCalculateColumn$ = new BehaviorSubject<Partial<CalculateColumnModel>>(null);
  private calculateColumnError$ = new BehaviorSubject<string>(null);

  // userFilter
  private userFilters$ = new BehaviorSubject<Partial<UserFilter>[]>([]);
  private userFilterDetails$ = new BehaviorSubject<UserFilterDetails>(null);
  isUserFilterDetailsError = signal<boolean>(false);
  private selectedUsersInUserFilter$ = new BehaviorSubject<Partial<UserInUserFilter>[]>([]);
  private usersAndGroups$ = new BehaviorSubject<UserOrGroup[]>([]);
  private selectedColumnValues$ = new BehaviorSubject<string[]>([]);
  private userFiltersTotalPages$ = new BehaviorSubject<number>(null);
  private userFiltersPageNumber$ = new BehaviorSubject<number>(1);

  //replacement data source
  private previewReplacementDataSourceInfo$ = new BehaviorSubject<Partial<PreviewDataSourceInfo>>(null);

  private workspaceId$ = new BehaviorSubject<string>(null);
  private dataSourceId$ = new BehaviorSubject<string>(null);
  private submitState$ = new BehaviorSubject<SubmitType>('notSubmitted');

  // conditional column
  private columnList$ = new BehaviorSubject<ColumnList[]>([]);
  private selectedConditionalColumn = new BehaviorSubject<DataSourceConditionalColumn>(null);

  private usedDashboards$ = new BehaviorSubject<string[]>([]);

  // custom date columns
  private customDateColumnsdata$ = new BehaviorSubject<CustomDateColumns[]>([]);
  private customDateColumnsInitialShamsiListColumns$ = new BehaviorSubject<DateColumnsListItem[]>(shamsiDateColumnTypes);
  private customDateColumnsInitialMiladiListColumns$ = new BehaviorSubject<DateColumnsListItem[]>(miladiDateColumnTypes);
  private processingDataSources$ = new BehaviorSubject<ProcessingDataSource[]>([]);
  private retryNeededDatasourceId$ = new BehaviorSubject<string>(null);

  // dataSource upload progress
  private dataSourceUploadProgress$ =  new BehaviorSubject<DataSourceUploadProgress[]>([]);
  private dataSourceUploadCanceled$ = new BehaviorSubject<string | null>(null)

  constructor(
    private dataModuleColumnService: DataModuleColumnService,
  ) {
  }

  // defaults
  setLoadingDataSource(loading: boolean): void {
    if (loading) {
      this.loadingScheduleHistory = true;
      this.loadingRelations = true;
      this.loadingUserFilters = true;
      this.loadingSchedule = true;
      this.loadingDataSourceColumnsDataTypes = true;
    }
    this.loadingDataSource$.next(loading);
  }

  getLoadingDataSource$(): Observable<boolean> {
    return this.loadingDataSource$.asObservable();
  }

  checkDataSourceIsLoaded(): void {
    if (!this.loadingDataSourceTable && !this.loadingRelations && !this.loadingScheduleHistory &&
      !this.loadingScheduleHistory && !this.loadingDataSourceColumnsDataTypes && !this.loadingUserFilters) {
      this.setLoadingDataSource(false);
    }
  }

  updateWorkspaceId(workspaceId: string): void {
    this.workspaceId$.next(workspaceId);
  }

  getWorkspaceId$(): Observable<string> {
    return this.workspaceId$.asObservable();
  }

  getWorkspaceId(): string {
    return this.workspaceId$.getValue();
  }

  updateDataSourceId(dataSourceId: string): void {
    this.dataSourceId$.next(dataSourceId);
  }

  getDataSourceId$(): Observable<string> {
    return this.dataSourceId$.asObservable();
  }

  getDataSourceId(): string {
    return this.dataSourceId$.getValue();
  }

  setSubmitState(submitted: SubmitType): void {
    this.submitState$.next(submitted);
  }

  getSubmitState$(): Observable<SubmitType> {
    return this.submitState$.asObservable();
  }

  // mainDataSource
  updateMainDataSource(dataSource: DataSource, relations: RelationData = null): void {
    if (dataSource !== null) {
      if (relations !== null) {
        dataSource.modelsPositions = new Map<string, { x: string; y: string }>();
        dataSource.relationData = relations;
        relations.inModels.forEach((modelDataSource: Model, index: number) => {
          this.setDataSourcePosition(dataSource, modelDataSource.dataSourceId, index);
        });
      }
      this.setMainDataSourcePosition(dataSource);
    }
    this.mainDataSource$.next(dataSource);
  }

  setDataSourceName(name: string): void {
    const dataSource = this.mainDataSource$.getValue();
    dataSource.name = name;
    this.mainDataSource$.next({...dataSource});
  }

  getMainDataSource$(): Observable<DataSource> {
    return this.mainDataSource$.asObservable().pipe(skip(1));
  }

  updateDataSourceTableContent(columns: Column[]) {
    this.tableContent$.next(columns);
  }

  deleteCustomColumn(columnId: string) {
    this.tableContent$.next([...this.tableContent$.getValue().filter(column => column.id !== columnId)]);
  }

  getCalculateColumnError$(): Observable<string> {
    return this.calculateColumnError$.asObservable();
  }

  updateCalculateColumnError(error: string) {
    this.calculateColumnError$.next(error);
  }

  updateColumnsDataType(columnsType: ColumnsType) {
    this.columnsType$.next(columnsType);
  }

  getColumnsType$(): Observable<ColumnsType> {
    return this.columnsType$.asObservable().pipe(skip(1));
  }

  getTableContent$(): Observable<Column[]> {
    return this.tableContent$.asObservable().pipe(skip(1));
  }

  // relations
  createRelation(relation: Relation, relationId: string): void {
    const updateMainDataSource = {...this.mainDataSource$.getValue()};
    relation.relationId = relationId;
    updateMainDataSource.relationData.inRelations.push(relation);
    this.mainDataSource$.next(updateMainDataSource);
  }

  updateRelation(relation: Relation) {
    const updateMainDataSource = {...this.mainDataSource$.getValue()};
    const foundIndex = updateMainDataSource.relationData.inRelations
      .findIndex(connection => connection.relationId === relation.relationId);
    updateMainDataSource.relationData.inRelations[foundIndex] = relation;
    this.mainDataSource$.next(updateMainDataSource);
  }

  convertDataSourcesSizeToKiloByte(dataSources: Partial<DataSource>[]): void {
    // if (dataSources.length > 0) {
    //   dataSources.forEach((dataSource) => {
    //     dataSource.size = (dataSource.size / 1000).toFixed(3);
    //   });
    // }
    this.dataSourcesForRelation$.next(dataSources);
  }

  getDataSourcesForRelation$(): Observable<Partial<DataSource>[]> {
    return this.dataSourcesForRelation$.asObservable().pipe(skip(1));
  }

  getDataSource$() {
    return this.mainDataSource$.asObservable();
  }

  addDataSourceInRelationDataInModels(dataSource: Model) {
    const mainDataSource = JSON.parse(JSON.stringify(this.mainDataSource$.getValue())) as unknown as DataSource;
    mainDataSource.permissions = this.mainDataSource$.getValue().permissions;
    const relations = this.mainDataSource$.getValue().relationData;
    mainDataSource.modelsPositions = this.mainDataSource$.getValue().modelsPositions;
    mainDataSource.position = this.mainDataSource$.getValue().position;
    mainDataSource.relationsModel.push(dataSource.dataSourceId);
    relations.inModels.push(dataSource);
    mainDataSource.relationData = relations;
    this.setDataSourcePosition(mainDataSource, dataSource.dataSourceId, relations.inModels.length - 1);
    this.mainDataSource$.next(mainDataSource);
  }

  deleteDataSourceFromRelationData(destinationDataSourceId: string) {
    const updateMainDataSource = {...this.mainDataSource$.getValue()};
    const relationData = updateMainDataSource.relationData;
    const foundIndexInModels = relationData.inModels.findIndex(model => model.dataSourceId === destinationDataSourceId);
    if (foundIndexInModels > -1) {
      relationData.inModels.splice(foundIndexInModels, 1);
    }

    relationData.inRelations.forEach((relation, index) => {
      if (relation.destinationDataSourceId === destinationDataSourceId ||
        relation.sourceDataSourceId === destinationDataSourceId) {
        relationData.inRelations.splice(index, 1);
      }
    });
    updateMainDataSource.modelsPositions.delete(destinationDataSourceId);
    this.mainDataSource$.next(updateMainDataSource);
  }

  toggleActivationRelation(relation: Relation, inactive: boolean): void {
    const updateMainDataSource = {...this.mainDataSource$.getValue()} as unknown as DataSource;
    const relationIndex = updateMainDataSource.relationData.inRelations
      .findIndex(connection => connection.relationId === relation.relationId);
    updateMainDataSource.relationData.inRelations[relationIndex].inactive = inactive;
    this.mainDataSource$.next(updateMainDataSource);
  }

  deleteRelation(relationId: string) {
    const updateMainDataSource = {...this.mainDataSource$.getValue()};
    const relationData = updateMainDataSource.relationData;
    const foundIndex = relationData.inRelations.findIndex(relation => relation.relationId === relationId);
    updateMainDataSource.relationData.inRelations.splice(foundIndex, 1);
    this.mainDataSource$.next(updateMainDataSource);
  }

  updateCreateRelationSubmitState(submitState: Submit): void {
    this.createRelationState$.next(submitState);
  }

  getCreateRelationSubmit$(): Observable<Submit> {
    return this.createRelationState$.asObservable();
  }

  setTotalPagesRelation(totalPages: number): void {
    this.totalPagesRelation$.next(totalPages);
  }

  getTotalPagesRelation$(): Observable<number> {
    return this.totalPagesRelation$.asObservable().pipe(skip(1));
  }

  setPageNumberRelation(pageNumber: number): void {
    this.pageNumberRelation$.next(pageNumber);
  }

  getPageNumberRelation$(): Observable<number> {
    return this.pageNumberRelation$.asObservable().pipe(skip(1));
  }

  setRelationState(relationState: RelationState): void {
    this.relationState$.next(relationState);
  }

  getRelationState$(): Observable<RelationState> {
    return this.relationState$.asObservable();
  }

  // schedule
  updateSchedule(newSchedule: ScheduleAsset) {
    if (!newSchedule) return;
      newSchedule.schedulePlan = this.schedulePlan(newSchedule);
    this.schedule$.next(newSchedule);
  }

  getScheduleOfAsset$(): Observable<ScheduleAsset> {
    return this.schedule$.asObservable().pipe(skip(1));
  }

  updateScheduleHistory(newScheduleHistory: ScheduleHistory[]) {
    if (newScheduleHistory !== null) {
      newScheduleHistory.forEach((schedule: ScheduleHistory) =>
        schedule.executeTime = Math.round(schedule.executeTime));
    }
    this.scheduleHistory$.next(newScheduleHistory);
  }

  getScheduleHistory$(): Observable<ScheduleHistory[]> {
    return this.scheduleHistory$.asObservable();
  }

  setIsUpdatingDatasource(isUpdating: boolean) {
    const mainDataSource = this.mainDataSource$.getValue();
    mainDataSource.isUpdating = isUpdating;
    this.mainDataSource$.next(mainDataSource);
  }

  toggleMainDataSourceShowAllFields(): void {
    const dataSource = this.mainDataSource$.getValue();
    dataSource.showAllFields = !dataSource.showAllFields;
    this.mainDataSource$.next(dataSource);
  }

  // webService
  updateWebServiceConnection(webServerConnection: WebServiceConnection): void {
    this.webServiceConnection$.next(webServerConnection);
  }

  getWebServiceConnection$(): Observable<WebServiceConnection> {
    return this.webServiceConnection$.asObservable().pipe(skip(1));
  }

  setScheduleHistoryTotalPages(totalPages: number) {
    this.scheduleHistoryTotalPages$.next(totalPages);
  }

  getScheduleHistoryTotalPages$(): Observable<number> {
    return this.scheduleHistoryTotalPages$.asObservable().pipe(skip(1));
  }

  setScheduleHistoryPageNumber(pageNumber: number) {
    this.scheduleHistoryPageNumber$.next(pageNumber);
  }

  getScheduleHistoryPageNumber$(): Observable<number> {
    return this.scheduleHistoryPageNumber$.asObservable().pipe(skip(1));
  }

  setUserFiltersTotalPages(totalPages: number) {
    this.userFiltersTotalPages$.next(totalPages);
  }

  getUserFiltersTotalPages$(): Observable<number> {
    return this.userFiltersTotalPages$.asObservable().pipe(skip(1));
  }

  setUserFiltersPageNumber(pageNumber: number) {
    this.userFiltersPageNumber$.next(pageNumber);
  }

  getUserFiltersPageNumber$(): Observable<number> {
    return this.userFiltersPageNumber$.asObservable().pipe(skip(1));
  }

  //calculate column
  getCalculateColumnSubmit$() {
    return this.calculateColumnSubmit$.asObservable();
  }

  updateCalculateColumnSubmit(submit: SubmitType) {
    this.calculateColumnSubmit$.next(submit);
  }

  getEditCalculateColumn$(): Observable<Partial<CalculateColumnModel>> {
    return this.editCalculateColumn$.asObservable();
  }

  updateEditCalculateColumn(editCalculateColumn: Partial<CalculateColumnModel>) {
    this.editCalculateColumn$.next({...editCalculateColumn});
  }

  // user filter
  updateUserFilters(userFilters: Partial<UserFilter>[]) {
    this.userFilters$.next(userFilters);
  }

  getUserFilters$(): Observable<Partial<UserFilter>[]> {
    return this.userFilters$.asObservable();
  }

  setUsersAndGroupsToAddUserFilter(usersAndUserGroups: UserOrGroup[]): void {
    this.usersAndGroups$.next(usersAndUserGroups);
  }

  getUsersAndGroups$(): Observable<UserOrGroup[]> {
    return this.usersAndGroups$.asObservable();
  }

  setSelectedColumnValues(values: string[]): void {
    this.selectedColumnValues$.next(values);
  }

  appendSelectedColumnValues(appendValues: string[]): void {
    const currentValues = this.selectedColumnValues$.getValue();
    this.selectedColumnValues$.next([...currentValues, ...appendValues.splice(1)]);
  }

  getSelectedColumnValues$(): Observable<string[]> {
    return this.selectedColumnValues$.asObservable();
  }

  setUserFilterDetails(userFilterDetails: UserFilterDetails): void {
    const users = [];
    if (userFilterDetails) {
      userFilterDetails.usersInUserFilter.forEach((tempUser, index) => {
        const selectedUser: Partial<UserInUserFilter> = {
          userFilterRecipientId: tempUser.userFilterRecipientId,
          userFilterRecipientType: tempUser.userFilterRecipientType,
          recipientName: tempUser.recipientName,
          isInclude: tempUser.isInclude,
          selectedValues: [...tempUser.selectedValues],
          index,
        };
        users.push(selectedUser);
      });
    }
    this.userFilterDetails$.next(userFilterDetails);
    this.selectedUsersInUserFilter$.next(users);
  }

  getUserFilterDetails$() {
    return this.userFilterDetails$.asObservable().pipe(skip(1));
  }

  setSelectedUserSInUserFilter(userFilters: Partial<UserInUserFilter>[]): void {
    this.selectedUsersInUserFilter$.next(userFilters);
  }

  getSelectedUsersInUserFilter$(): Observable<Partial<UserInUserFilter>[]> {
    return this.selectedUsersInUserFilter$.asObservable();
  }

  getUserFilterRecipients(updateUsersInUserFilter: Partial<UserInUserFilter>[],
                          type: 'Edit' | 'Create'): Partial<UserFilterRecipients>[] {
    const userFilterRecipients: Partial<UserFilterRecipients>[] = [];
    if (type === 'Edit') {
      const usersInUserFilter = this.userFilterDetails$.getValue().usersInUserFilter;
      usersInUserFilter.forEach(tempUser => {
        const findIndex = updateUsersInUserFilter.findIndex
        (tempSelectedUser =>
          tempSelectedUser.userFilterRecipientId === tempUser.userFilterRecipientId);
        if (findIndex > -1) {
          const statusUpdated = !this.hasEqualValues
            (updateUsersInUserFilter[findIndex].selectedValues, tempUser.selectedValues) ||
            updateUsersInUserFilter[findIndex].isInclude !== tempUser.isInclude;
          if (statusUpdated) {
            userFilterRecipients.push(this.addUserFilterRecipients(tempUser.userFilterRecipientId,
              tempUser.userFilterRecipientType, updateUsersInUserFilter[findIndex].selectedValues,
              updateUsersInUserFilter[findIndex].isInclude, 'Updated'));
          }
          updateUsersInUserFilter.splice(findIndex, 1);
        } else {
          userFilterRecipients.push(this.addUserFilterRecipients(tempUser.userFilterRecipientId,
            tempUser.userFilterRecipientType, undefined, undefined, 'Removed'));
        }
      });
    }
    updateUsersInUserFilter.forEach(tempUser => {
      userFilterRecipients.push(this.addUserFilterRecipients(tempUser.userFilterRecipientId,
        tempUser.userFilterRecipientType, tempUser.selectedValues, tempUser.isInclude, 'New'));
    });
    return userFilterRecipients;
  }

  addUserToUserFiler(userOrGroup: UserOrGroup): void {
    const selectedUsers = this.selectedUsersInUserFilter$.getValue();
    selectedUsers.push(
      {
        userFilterRecipientId: userOrGroup.userFilterRecipientId,
        userFilterRecipientType: userOrGroup.userFilterRecipientType,
        recipientName: userOrGroup.recipientName,
        selectedValues: [],
        index: selectedUsers.length,
        isInclude: true,
      });
    this.selectedUsersInUserFilter$.next(selectedUsers);
  }

  removeUserFromUserFilter(userInUserFilter: Partial<UserInUserFilter>): void {
    const selectedUsers = this.selectedUsersInUserFilter$.getValue();
    if (userInUserFilter?.index > -1) {
      selectedUsers.splice(userInUserFilter.index, 1);
      selectedUsers.forEach((tempItem, index, self) => {
        self[index].index = index;
      });
    }
    this.selectedUsersInUserFilter$.next(selectedUsers);
  }

  hasEqualValues(updateValues: string[], currentValues: string[]) {
    if (updateValues.length !== currentValues.length) {
      return false;
    }

    for (let i = 0; i < updateValues.length; i++) {
      let valueExist = false;
      currentValues.forEach(currentValue => {
        if (currentValue === updateValues[i]) {
          valueExist = true;
        }
      });
      if (!valueExist) {
        return false;
      }
    }
    return true;
  }

  addUserFilterRecipients(userFilterRecipientId: string, userFilterRecipientType: UserFilterRecipientType,
                          selectedValues: string[], isInclude: boolean,
                          userFilterRecipientStatus: UserFilterRecipientStatus): Partial<UserFilterRecipients> {
    return {
      userFilterRecipientId,
      userFilterRecipientType,
      selectedValues,
      isInclude,
      userFilterRecipientStatus,
    };
  }

  // conditional column
  loadColumnList(): void {
    const dataSourceColumn = this.mainDataSource$.getValue().columns;
    const columnList: ColumnList[] = [];
    dataSourceColumn.forEach(column => {
      columnList.push({
        name: column.name,
        id: column.id,
        dataModuleType: column.dataModuleType,
        icon: this.dataModuleColumnService.getIcon(new DataSourceDataModuleColumn(column)),
      });
    });
    this.columnList$.next(columnList);
  }

  getColumnList$(): Observable<ColumnList[]> {
    return this.columnList$.asObservable();
  }

  setConditionalColumn(conditionalColumn: DataSourceConditionalColumn): void {
    this.selectedConditionalColumn.next(conditionalColumn);
  }

  getConditionalColumn$(): Observable<DataSourceConditionalColumn> {
    return this.selectedConditionalColumn.asObservable().pipe(skip(1));
  }

  updatePreviewDataSourceInfo(dataSourceInfo: Partial<PreviewDataSourceInfo>): void {
    this.previewReplacementDataSourceInfo$.next(dataSourceInfo);
  }

  getPreviewDataSourceInfo$(): Observable<Partial<PreviewDataSourceInfo>> {
    return this.previewReplacementDataSourceInfo$.asObservable();
  }

  setUsedDashboards(usedDashboards: string[]): void {
    this.usedDashboards$.next(usedDashboards);
  }

  getUsedDashboards$(): Observable<string[]> {
    return this.usedDashboards$.asObservable();
  }

  getUsedDashboardsValue(): string[] {
    return this.usedDashboards$.value;
  }

  setCustomDateColumnsData(columnsData: CustomDateColumns[]){
    this.customDateColumnsdata$.next(columnsData);
  }

  getCustomDateColumnsData(): Observable<CustomDateColumns[]> {
    return this.customDateColumnsdata$.asObservable();
  }

  setCustomDateColumnsListDataShamsi(columnsData: DateColumnsListItem[]){
    this.customDateColumnsInitialShamsiListColumns$.next(columnsData);
  }

  getCustomDateColumnsListDataShamsi(): Observable<DateColumnsListItem[]> {
    return this.customDateColumnsInitialShamsiListColumns$.asObservable();
  }

  setCustomDateColumnsListDataMiladi(columnsData: DateColumnsListItem[]){
    this.customDateColumnsInitialMiladiListColumns$.next(columnsData);
  }

  getCustomDateColumnsListDataMiladi(): Observable<DateColumnsListItem[]> {
    return this.customDateColumnsInitialMiladiListColumns$.asObservable();
  }
  // DataSourceState
  setProcessingDataSources(dataSources: ProcessingDataSource[]): void {
    this.processingDataSources$.next([...dataSources]);
  }

  getProcessingDataSources$() {
    return this.processingDataSources$.asObservable();
  }

  retryProcessing$(){
    return this.retryNeededDatasourceId$.asObservable();
  }
  // private methods
  private schedulePlan(newSchedule: ScheduleAsset): string {

    const weekDays = [
      {id: WeekDay.Saturday, name: 'شنبه'},
      {id: WeekDay.Sunday, name: 'یکشنبه'},
      {id: WeekDay.Monday, name: 'دوشنبه'},
      {id: WeekDay.Tuesday, name: 'سه‌شنبه'},
      {id: WeekDay.Wednesday, name: 'چهارشنبه'},
      {id: WeekDay.Thursday, name: 'پنجشنبه'},
      {id: WeekDay.Friday, name: 'جمعه'},
    ];
    const weekDayNumbers = [
      {id: WeekDayNumber.First, name: 'اولین'},
      {id: WeekDayNumber.Second, name: 'دومین'},
      {id: WeekDayNumber.Third, name: 'سومین'},
      {id: WeekDayNumber.Fourth, name: 'چهارمین'},
    ];
    let schedulePlan = '-';
    switch (newSchedule?.scheduleType) {
      case ScheduleType.Minute:
        if (newSchedule.numberOfScheduleType > 0) {
          schedulePlan = 'هر ' + newSchedule.numberOfScheduleType + ' دقیقه یک بار';
        }
        break;
      case ScheduleType.Hour:
        if (newSchedule.numberOfScheduleType > 0) {
          schedulePlan = ' هر' + newSchedule.numberOfScheduleType + ' ساعت یک بار ';
        }
        break;
      case ScheduleType.Day:
        schedulePlan = newSchedule.everyWeekDay === true ?
          'هر روز (شنبه تا جمعه)' : 'روزهای کاری (شنبه تا چهارشنبه)';
        break;
      case ScheduleType.Week:
        if (newSchedule.specificDays !== null) {
          let days = '';
          newSchedule.specificDays.forEach(day => {
            const index = weekDays.findIndex(weekDay => weekDay.id === day);
            if (days === '') {
              days += ' ' + weekDays[index].name;
            } else {
              days += ' ,' + weekDays[index].name;
            }
          });
          if (newSchedule.specificDays.length === 1) {
            schedulePlan = ' هر هفته در روز' + days;
          } else {
            schedulePlan = ' هر هفته در روزهای ' + days;
          }
        }
        break;
      case ScheduleType.Month:
        if (newSchedule.everyMonthNumber > 0) {
          const monthNumber = newSchedule.everyMonthNumber;
          if (newSchedule.numberOfScheduleType > 0) {
            const dayNumber = newSchedule.numberOfScheduleType;
            schedulePlan = 'هر ' + monthNumber + ' ماه یکبار، در روز ' + dayNumber + ' ام ماه';
          }

          if (newSchedule.weekDayNumber !== null) {
            const indexWeekDayNumbers = weekDayNumbers.findIndex(day => day.id === newSchedule.weekDayNumber);
            const weekDayNumber = weekDayNumbers[indexWeekDayNumbers].name;
            const indexWeekDay = weekDays.findIndex(day => day.id === newSchedule.weekDay);
            const weekDay = weekDays[indexWeekDay].name;
            schedulePlan = 'هر ' + monthNumber + ' ماه یکبار در ' + weekDayNumber + ' ' + weekDay + ' ماه';
          }
        }
        break;
      case ScheduleType.Year:
        if (newSchedule.monthType !== MonthType.None) {
          const monthType = newSchedule.monthType;

          if (newSchedule.numberOfScheduleType > 0) {
            const dayNumber = newSchedule.numberOfScheduleType;
            schedulePlan = ' هر سال در روز ' + dayNumber + ' ام ماه ' + monthType;
          }

          if (newSchedule.weekDayNumber !== null) {
            const indexWeekDayNumbers = weekDayNumbers.findIndex(day => day.id === newSchedule.weekDayNumber);
            const weekDayNumber = weekDayNumbers[indexWeekDayNumbers].name;
            const indexWeekDay = weekDays.findIndex(day => day.id === newSchedule.weekDay);
            const weekDay = weekDays[indexWeekDay].name;
            schedulePlan = ' هر سال در ' + weekDayNumber + ' ' + weekDay + ' ماه ' + monthType;
          }
        }
        break;
      default:
        schedulePlan = '-';
    }
    return schedulePlan;
  }

  private setDataSourcePosition(dataSource: DataSource, modelDataSourceId: string, index: number): void {
    let j: number;
    let x = '';
    switch (index % 4) {
      case 0:
        x = 'calc(50% - 650px)';
        j = index / 4;
        break;
      case 1:
        x = 'calc(50% - 380px)';
        j = (index - 1) / 4;
        break;
      case 2:
        x = 'calc(50% + 760px)';
        j = (index - 2) / 4;
        break;
      case 3:
        x = 'calc(50% + 1030px)';
        j = (index - 3) / 4;
        break;
    }
    const y = `${j * 340 + 20}px`;
    dataSource.modelsPositions.set(modelDataSourceId, {x, y});
  }

  private setMainDataSourcePosition(dataSource: DataSource) {
    dataSource.position = {
      x: 'calc(50% + 200px)',
      y: 'calc(50% + 300px)',
    };
  }

  // handle data source upload progress
  getDataSourceUploadProgress(): Observable<DataSourceUploadProgress[]> {
    return this.dataSourceUploadProgress$.asObservable()
  }

  updateDataSourceUploadProgress(newProgress: DataSourceUploadProgress): void {
    const currentProgress = this.dataSourceUploadProgress$.value;
    const findIndex = currentProgress.findIndex(progress => progress.dataSourceId === newProgress.dataSourceId);
    if (findIndex !== -1) {
      const existingItem = currentProgress[findIndex];
      if (existingItem.completionPercentage === 100) {
        const updated = [
          ...currentProgress.slice(0, findIndex),
          ...currentProgress.slice(findIndex + 1),
          { ...newProgress, isExpanded: true }
        ];
        this.dataSourceUploadProgress$.next(updated);
        return;
      }
      
      const updatedProgress = [...currentProgress];
      updatedProgress[findIndex].completionPercentage = newProgress.completionPercentage;
      updatedProgress[findIndex].hasError = newProgress.hasError;
      this.dataSourceUploadProgress$.next(updatedProgress);
    } else {
      this.dataSourceUploadProgress$.next([...currentProgress, { ...newProgress, isExpanded: true }]);
    }
  }

  cancelDataSourceUploadProgress(dataSourceId: string): void {
    const currentProgress = this.dataSourceUploadProgress$.value;
    const updatedProgress = currentProgress.filter((prog: DataSourceUploadProgress) => {
      prog.dataSourceId !== dataSourceId
    });

    this.dataSourceUploadProgress$.next(updatedProgress);
    this.updateDataSourceCanceledSubject(dataSourceId)
  }

  updateDataSourceCanceledSubject(value: string | null): void {
    this.dataSourceUploadCanceled$.next(value)
  }

  getDataSourceCanceledSubject(): Observable<string | null> {
    return this.dataSourceUploadCanceled$.asObservable()
  }
}
